import { useMutation } from "@apollo/client";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import { REVIEW_ENFORCEMENT_INTENT, ReviewEnforcementIntentData, ReviewEnforcementIntentVars } from "common/graphql";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React from "react";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

export const NoticeReviewActions: React.FC<NoticeReviewActionsProps> = ({ id }) => {
  const [isVoidDialogOpen, setIsVoidDialogOpen] = React.useState(false);
  const [deniedReason, setDeniedReason] = React.useState<{ label: string; value: string } | null>(null);
  const { publishSnackbarMessage } = useSnackPackContext();
  const [isVoiding, setIsVoiding] = React.useState(false);
  const [isIssuing, setIsIssuing] = React.useState(false);
  const navigate = useNavigate();

  const [reviewEnforcementIntent, { loading: isSubmitting }] = useMutation<
    ReviewEnforcementIntentData,
    ReviewEnforcementIntentVars
  >(REVIEW_ENFORCEMENT_INTENT);

  const handleCloseVoidDialog = () => {
    setIsVoidDialogOpen(false);
    setDeniedReason(null);
  };

  // TODO: handle error and success
  const handleVoidEnforcementIntent = () => {
    setIsVoiding(true);
    reviewEnforcementIntent({
      variables: { intentId: id, approve: false, deniedReason: deniedReason?.value || "" },
      onError() {
        setIsVoiding(false);
      },
      onCompleted() {
        setIsVoiding(false);
        publishSnackbarMessage({
          message: "Void successful",
          severity: "error",
        });
        navigate(-1);
      },
    });
  };

  const handleIssueEnforcementIntent = () => {
    setIsIssuing(true);
    reviewEnforcementIntent({
      variables: { intentId: id, approve: true },
      onError() {
        setIsIssuing(false);
      },
      onCompleted() {
        setIsIssuing(false);
        publishSnackbarMessage({
          message: "Issue successful",
          severity: "success",
        });
        navigate(-1);
      },
    });
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2 }}>
      <Button disabled={isSubmitting} variant="contained" color="error" onClick={() => setIsVoidDialogOpen(true)}>
        Void
      </Button>
      <LoadingButton
        disabled={isSubmitting}
        loading={isIssuing}
        variant="contained"
        color="primary"
        onClick={handleIssueEnforcementIntent}
      >
        Issue
      </LoadingButton>
      <Dialog open={isVoidDialogOpen} onClose={handleCloseVoidDialog} sx={{ px: 4 }}>
        <DialogTitle>
          <Typography variant="h4">Voiding a Notice?</Typography>
          <Typography variant="body1">Select from the below options</Typography>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="subtitle1">Tell us what is wrong with the notice</Typography>
            <Typography variant="caption">
              Fill out this field if you'd like! It really helps our team at Automotus make any necessary updates to our
              software if there's an issue we can address.
              <br /> <br />
              Thanks for your help!
            </Typography>
          </Box>
          <Box sx={{ mt: 4 }}>
            <Autocomplete
              options={VOID_REASONS}
              fullWidth
              disabled={isSubmitting}
              value={deniedReason}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => setDeniedReason(value)}
              renderInput={(params) => <TextField {...params} required label="Reason for Void" />}
            />
            <Stack sx={{ mt: 6 }} spacing={3}>
              <LoadingButton
                loading={isVoiding}
                disabled={!deniedReason || isSubmitting}
                fullWidth
                variant="contained"
                color="error"
                onClick={handleVoidEnforcementIntent}
              >
                Void Notice
              </LoadingButton>
              <Button disabled={isSubmitting} fullWidth variant="contained" onClick={handleCloseVoidDialog}>
                Cancel
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const VOID_REASONS = [
  "Armored Car",
  "Consul Plate",
  "Dark Environment",
  "Equipment Malfunction",
  "Fake Plate",
  "Government Vehicle",
  "Legal Event",
  "Location Error",
  "No Plate",
  "No Violation",
  "Plate Glare",
  "Plate Obstructed",
  "Poor Image",
  "Tow Action",
  "Unreadable License Plate",
  "Vehicle Moving",
  "Wrong License Plate Number",
  "Other",
].map((reason) => ({ label: reason, value: reason.toLowerCase() }));

export interface NoticeReviewActionsProps {
  id: string;
}

export default NoticeReviewActions;
